/**
 * Permission codes the Accounts Payable screens declare an interest in.
 *
 * They are the dot-strings `internal/ap/permissions.go` registers with the RBAC
 * catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once here
 * means a renamed permission is a compile error rather than a silently invisible
 * button.
 *
 * Three groups of them are not ordinary read/write pairs and the screens treat
 * them accordingly.
 *
 * **The invoice trio is a declared segregation-of-duties set.** `core/rbac`
 * names `ap.invoice.create`, `ap.invoice.approve` and `ap.payment.execute` in
 * its conflict pairs: the person who enters a bill must not be the person who
 * approves it, nor the person who pays it. So Save, Submit, Approve, Post and
 * Pay are five independently permissioned actions on the document screen and
 * none of them is inferred from another.
 *
 * **The bank codes separate seeing from changing from revealing.** Reading a
 * vendor's bank account (masked) is an ordinary part of the job;
 * `ap.vendor.bank.change` only *requests* a change, `ap.vendor.bank.approve` is
 * the second pair of eyes that applies it, and `ap.vendor.bank.reveal` decrypts
 * an account number and is audited by name. The last three declare
 * `RequiresMFA`, so a session with no recent second factor is refused with
 * `ERR_STEP_UP_REQUIRED` — which the screens treat as a guided step, not an
 * error toast.
 *
 * **`ap.payment.execute` moves money**, and it is also what the bank-file route
 * is guarded by, because that file contains beneficiary account numbers in the
 * clear.
 */

export const AP_PERMISSIONS = {
  vendorRead: 'ap.vendor.read',
  vendorCreate: 'ap.vendor.create',
  vendorUpdate: 'ap.vendor.update',
  vendorDelete: 'ap.vendor.delete',

  /** Read bank details **masked**. Never enough to see an account number. */
  bankRead: 'ap.vendor.bank.read',
  /** Request a change. Applies nothing on its own (§8.3 dual approval). */
  bankChange: 'ap.vendor.bank.change',
  /** Approve somebody else's request. Never one's own. */
  bankApprove: 'ap.vendor.bank.approve',
  /** Decrypt an account number. Audited under the reader's name. */
  bankReveal: 'ap.vendor.bank.reveal',

  invoiceRead: 'ap.invoice.read',
  invoiceCreate: 'ap.invoice.create',
  invoiceUpdate: 'ap.invoice.update',
  invoiceDelete: 'ap.invoice.delete',
  invoiceSubmit: 'ap.invoice.submit',
  invoiceApprove: 'ap.invoice.approve',
  invoicePost: 'ap.invoice.post',
  invoiceReverse: 'ap.invoice.reverse',
  /** Place a payment hold, with a reason (FIN-F02, FT2-B5). */
  invoiceHold: 'ap.invoice.hold',
  /** Lift a payment hold, with a reason. Its own permission, no self-release rule. */
  invoiceReleaseHold: 'ap.invoice.release_hold',
  /** Read purchase orders, to offer one to match a bill to. */
  purchaseOrderRead: 'procurement.po.read',
  /** Raise a supplier block request (P45 B64): Finance Manager, then CFO, decide it. */
  supplierBlock: 'procurement.supplier.block',
  /** A block disposition that cancels / short-closes an order needs that order permission. */
  purchaseOrderCancel: 'procurement.po.cancel',
  purchaseOrderClose: 'procurement.po.close',

  paymentRead: 'ap.payment.read',
  paymentCreate: 'ap.payment.create',
  paymentApprove: 'ap.payment.approve',
  /** Executes the run and produces the bank file. Step-up MFA, both times. */
  paymentExecute: 'ap.payment.execute',

  /**
   * Guards both reading and writing `/ap-settings` (posting accounts, settlement
   * tolerance, bank-change cooling-off). There is no separate read permission.
   */
  settingsManage: 'ap.settings.manage',

  /* ---------------------------------------------- C3 AP controls (§6.1) */

  signalRead: 'ap.signal.read',
  signalDispose: 'ap.signal.dispose',
  /**
   * Step-up MFA, and SoD-paired against `ap.vendor.bank.approve`: without the
   * pair, one person could approve a change to a vendor's paying account and
   * dissolve its cooling-off in the same minute.
   */
  bankChangeRelease: 'ap.vendor_bank.release_early',
  /** Step-up MFA. Raises or resolves a returned / recalled / stopped payment. */
  paymentException: 'ap.payment.exception',
  reportRead: 'ap.report.read',
  /** Read the withholding tax register (FIN-FR-006, FT7-B30). */
  whtRead: 'ap.wht.read',

  /** Read the chart of accounts to offer accounts as line and bank choices. */
  accountRead: 'org.account.read',
  /** Read companies and their dimension trees to scope a document. */
  companyRead: 'org.company.read',
  unitRead: 'org.unit.read',
} as const

export type ApPermission = (typeof AP_PERMISSIONS)[keyof typeof AP_PERMISSIONS]
